/*
 * <card-loop-video>
 *
 * Collection-card port of the PDP looping video (product-loop-video.js): an
 * auto-playing, muted, looping product video in the card carousel's 3rd slot,
 * driven by the custom.pdp_loop_video metafield. The play/pause toggle sits
 * bottom-LEFT (quick add owns the card's bottom-right).
 *
 * Performance: the server renders NO <video> — just a lazy poster image and
 * data attributes. The <video> is created on demand, the first time the
 * shopper engages that card's carousel (scrolls its slider) or the slide
 * itself becomes visible. A 36-card grid therefore makes zero video requests
 * until a card is actually swiped. Playback runs only while the slide is at
 * least half visible (the IntersectionObserver respects the horizontal
 * scroller's clipping, so a slide swiped away or a card scrolled off the page
 * both pause).
 *
 * Swap-safe like the PDP element: all wiring lives in connectedCallback /
 * disconnectedCallback, so cards replaced by AJAX filtering or the color
 * swatch swap re-initialise cleanly with no leaked observers or listeners.
 */
if (!customElements.get('card-loop-video')) {
  customElements.define(
    'card-loop-video',
    class CardLoopVideo extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('.card-loop-video__toggle');
        this.ring = this.querySelector('.card-loop-video__ring-progress');
        this.link = this.querySelector('a');
        if (!this.toggle || !this.dataset.videoSrc) return;

        // While this slide is showing, CSS lifts the card's corner badge (SALE
        // etc.) above the play/pause control — both sit bottom-left otherwise
        // (see card-product-image-slider.css).
        this.card = this.closest('.card');

        this.video = null;
        this.userPaused = false;
        this.inView = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.labelPlay = this.toggle.dataset.labelPlay || 'Play video';
        this.labelPause = this.toggle.dataset.labelPause || 'Pause video';

        this.onToggleClick = this.onToggleClick.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onScrollerScroll = this.onScrollerScroll.bind(this);
        this.tick = this.tick.bind(this);

        this.toggle.addEventListener('click', this.onToggleClick);

        // Warm signal: the shopper started swiping this card's carousel (touch
        // swipe and the desktop arrows both scroll the list), so start fetching
        // the video before its slide arrives.
        this.scroller = this.closest('.card-image-slider__list');
        if (this.scroller) this.scroller.addEventListener('scroll', this.onScrollerScroll, { passive: true });

        // Play only while the slide is the dominant one on screen; pause
        // otherwise. Any visibility at all also works as a warm signal.
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
              if (this.card) this.card.classList.toggle('card--video-slide-active', this.inView);
              if (entry.isIntersecting) this.warm();
              if (this.inView) this.maybePlay();
              else if (this.video) this.video.pause();
            });
          },
          { threshold: [0, 0.5, 1] }
        );
        this.observer.observe(this);

        this.setPlayingState(false);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.card) this.card.classList.remove('card--video-slide-active');
        if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
        if (this.scroller) this.scroller.removeEventListener('scroll', this.onScrollerScroll);
        if (this.video) {
          this.video.removeEventListener('play', this.onPlay);
          this.video.removeEventListener('pause', this.onPause);
          this.video.pause();
        }
        this.stopTicking();
      }

      onScrollerScroll() {
        this.scroller.removeEventListener('scroll', this.onScrollerScroll);
        this.warm();
      }

      // Create the video so it starts buffering, without playing it yet.
      // Reduced-motion users don't get a speculative download — for them the
      // video is only created by an explicit press of the play button.
      warm() {
        if (this.video || this.reducedMotion) return;
        this.createVideo();
      }

      createVideo() {
        if (this.video) return this.video;
        const video = document.createElement('video');
        video.className = 'card-loop-video__video';
        video.muted = true; // property AND attribute: iOS only autoplays muted video
        video.setAttribute('muted', '');
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'auto';
        video.disablePictureInPicture = true;
        video.setAttribute('disableremoteplayback', '');
        video.setAttribute('tabindex', '-1');
        if (this.dataset.videoLabel) video.setAttribute('aria-label', this.dataset.videoLabel);
        const source = document.createElement('source');
        source.src = this.dataset.videoSrc;
        if (this.dataset.videoType) source.type = this.dataset.videoType;
        video.appendChild(source);
        video.addEventListener('play', this.onPlay);
        video.addEventListener('pause', this.onPause);
        (this.link || this).appendChild(video);
        this.video = video;
        return video;
      }

      maybePlay() {
        if (this.userPaused || this.reducedMotion) return;
        const playPromise = this.createVideo().play();
        if (playPromise && typeof playPromise.catch === 'function') {
          // Swallow autoplay rejections (rare for muted inline video).
          playPromise.catch(() => {});
        }
      }

      onToggleClick() {
        if (!this.video || this.video.paused) {
          // Explicit user intent overrides both manual-pause and reduced-motion.
          this.userPaused = false;
          this.reducedMotion = false;
          this.maybePlay();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
      }

      onPlay() {
        this.setPlayingState(true);
        this.startTicking();
      }

      onPause() {
        this.setPlayingState(false);
        this.stopTicking();
        // Resume after an *external* pause (e.g. window.pauseAllMedia() fired by
        // the quick-add modal or cart drawer) while the slide is still on screen
        // and the user has not paused it themselves.
        if (!this.userPaused && !this.reducedMotion && this.inView && !this.resuming) {
          this.resuming = true;
          requestAnimationFrame(() => {
            this.resuming = false;
            if (!this.userPaused && this.inView && this.video && this.video.paused) this.maybePlay();
          });
        }
      }

      setPlayingState(playing) {
        this.classList.toggle('is-paused', !playing);
        this.toggle.setAttribute('aria-pressed', String(playing));
        this.toggle.setAttribute('aria-label', playing ? this.labelPause : this.labelPlay);
      }

      startTicking() {
        this.stopTicking();
        this.raf = requestAnimationFrame(this.tick);
      }

      stopTicking() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = null;
      }

      tick() {
        const duration = this.video && this.video.duration;
        if (this.ring && duration && isFinite(duration)) {
          const pct = Math.max(0, Math.min(100, (this.video.currentTime / duration) * 100));
          // pathLength="100" normalises the path, so offset = remaining percent.
          this.ring.style.strokeDashoffset = (100 - pct).toFixed(2);
        }
        this.raf = requestAnimationFrame(this.tick);
      }
    }
  );
}
